import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider, IconButton, Icon, Tooltip } from '@material-ui/core';
import { useParams, Link, useHistory } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import CarrierDetailAddMasterTicket from '../components/carrier-detail-add-master-ticket';
import CarrierDetailEdit from '../components/carrier-detail-edit';

import AuthContext from '../../context';
import { get, del, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    highlightedNumber: {
        color: '#0d6efd'
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 24
    },
    buttonContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 5
    },
    addMasterBtn: {
        backgroundColor: '#198754',
        borderColor: '#198754',
        '&:hover': {
            backgroundColor: '#198754',
            borderColor: '#198754',
            boxShadow: 'none'
        }
    },
    calculateBtn: {
        marginRight: 15,
        backgroundColor: '#6E6E6E',
        borderColor: '#6E6E6E',
        '&:hover': {
            backgroundColor: '#6E6E6E',
            borderColor: '#6E6E6E',
            boxShadow: 'none'
        }
    },
    tableContainer: {
        width: '100%',
        '& .total': {
            backgroundColor: '#E0F8E0'
        }
    },
    editBtn: {
        marginRight: 15,
        backgroundColor: '#ffc107',
        borderColor: '#ffc107',
        '&:hover': {
            backgroundColor: '#ffc107',
            borderColor: '#ffc107',
            boxShadow: 'none'
        }
    },
    deleteBtn: {
        marginRight: 15,
        backgroundColor: '#dc3545',
        borderColor: '#dc3545',
        '&:hover': {
            backgroundColor: '#dc3545',
            borderColor: '#dc3545',
            boxShadow: 'none'
        }
    }
}));

const defMaster = { id: 0, name: 'Choose a master ticket' };

const CarrierDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const sede = JSON.parse(localStorage.getItem('@sede'));
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [state, setState] = useState({
        loading: true,
        carrier: {
            idticket_carrier: 0,
            date: '',
            amount: 0,
            container: '',
            carrier_catalog: {},
            calculated: false,
            total_qty: 0,
            freight_per_piece: 0
        },
        masters: [],
        selectedMaster: defMaster,
        mastersToAdd: [defMaster],
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Carrier - detail';

        makeRequest();
    }, []);

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/ticketCarrier/${id}`);
        if (response.status === 200) {
            const { data, masters, mastersToAdd } = response;
            setState(prev => ({
                ...prev,
                loading: false,
                carrier: data,
                masters,
                mastersToAdd
            }));
        } else {
            console.log('[CarrierDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const toggleShow = form => {
        if (form === 'add') {
            setShowEditForm(false);
            setShowAddForm(!showAddForm);
        }
        if (form === 'edit') {
            setShowAddForm(false);
            setShowEditForm(!showEditForm);
        }
    }

    const removeMasterTicket = async idticket_master => {
        const cond = window.confirm('Are you sure you want add this master ticket?');
        if (cond) {
            showLoader(true);
            const response = await del(`/ticketCarrier/${id}/removeMasterTicket`, { idticket_master });
            if (response.status === 200) {
                const { message, masters, mastersToAdd } = response;
                showToast(message);
                setMasters(masters, mastersToAdd);
            } else {
                console.log('[CarrierDetail] removeMasterTicket error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const setMasters = (masters, mastersToAdd) => setState(prev => ({ ...prev, masters, mastersToAdd }));

    const setCarrier = carrier => setState(prev => ({ ...prev, carrier }));

    const calculateFreight = async () => {
        const cond = window.confirm('Are you sure you want calculate the freight of this carrier?');
        if (cond) {
            showLoader(true);
            const response = await put(`/ticketCarrier/${id}/calculateFreight`);
            if (response.status === 200) {
                setCarrier(response.data);
                showToast(response.message);
            } else {
                console.log('[CarrierDetail] calculateFreight error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const cond = window.confirm('Are you sure you want to delete this carrier?');
        if (cond) {
            showLoader(true);
            const response = await del(`/ticketCarrier/${id}`);
            if (response.status === 200) {
                history.replace('/ticket/ticketCarrier');
                showToast(response.message);
            } else {
                console.log('[CarrierDetail] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const valueGetter = ({ field, row }) => row.product_type_ref[field]?.name ?? 'N/A';
    const getTotalField = ({ value }) => `$ ${value}`;
    const renderCell = ({ row }) => {
        return (
            <Tooltip title='Show detail' placement='bottom'>
                <Link to={`/ticket/tickets/${row.idticket}`} target='_blank'>
                    <IconButton>
                        <Icon>{'visibility'}</Icon>
                    </IconButton>
                </Link>
            </Tooltip>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 0.7, minWidth: 98 },
        { field: 'state', headerName: 'Status', flex: 0.5, minWidth: 70 },
        { field: 'product', headerName: 'Product', flex: 1, minWidth: 140, valueGetter },
        { field: 'type', headerName: 'Type', flex: 0.7, minWidth: 98, valueGetter },
        { field: 'total_processing_qty', headerName: 'Qty', flex: 0.7, minWidth: 98 },
        { field: 'total', headerName: 'Total', flex: 0.8, minWidth: 112, valueGetter: getTotalField },
        { field: 'detail', headerName: 'Detail', flex: 0.5, minWidth: 70, renderCell }
    ]

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr || !state.carrier?.idticket_carrier) return <NotFound />

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Date'
                                value={dayjs(state.carrier.date).format('MM/DD/YYYY')}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Amount'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Amount'
                                value={`$ ${state.carrier.amount}`}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Container'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='N/A'
                                value={state.carrier?.container ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Carrier'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Carrier'
                                value={state.carrier.carrier_catalog?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Sede'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Sede'
                                value={sede?.name ?? '?'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Calculated'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Calculated'
                                value={state.carrier.calculated ? '✓' : '✗'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Total qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Total qty'
                                value={state.carrier.total_qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Freight per piece'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Freight pp'
                                value={`$ ${state.carrier.freight_per_piece}`}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonsContainer}>
                {
                    (state.permissions?.update_attr && state.masters?.length >= 1) &&

                    <Button
                        variant='contained'
                        className={classes.calculateBtn}
                        onClick={calculateFreight}
                    >
                        {'Calculate freight'}
                    </Button>
                }
                {
                    (state.permissions?.delete_attr && state.masters?.length < 1) &&
                    <Button
                        variant='contained'
                        className={classes.deleteBtn}
                        onClick={destroy}
                    >
                        {'Delete'}
                    </Button>
                }
                {
                    (state.permissions?.update_attr && state.masters?.length < 1) &&
                    <Button
                        variant='contained'
                        className={classes.editBtn}
                        onClick={() => toggleShow('edit')}
                    >
                        {'Edit'}
                    </Button>
                }
                {
                    state.permissions?.update_attr &&
                    <Button
                        variant='contained'
                        className={classes.addMasterBtn}
                        onClick={() => toggleShow('add')}
                    >
                        {'Add master ticket'}
                    </Button>
                }
            </div>

            <CarrierDetailAddMasterTicket
                open={showAddForm}
                id={id}
                masters={state.mastersToAdd}
                permissions={state.permissions}
                setMasters={setMasters}
                onClose={() => setShowAddForm(false)}
            />

            <CarrierDetailEdit
                open={showEditForm}
                id={id}
                carrier={state.carrier}
                setCarrier={setCarrier}
                permissions={state.permissions}
                onClose={() => setShowEditForm(false)}
            />

            <Divider className={classes.divider} />
            <h2>{'Master tickets'}</h2>
            <Divider className={classes.divider} />

            {
                (state.masters.length > 0) ?
                    state.masters.map(master =>
                        <div key={master.idticket_master}>
                            <Grid container>
                                <Grid item xs={12} lg={3}>
                                    <FormGroup>
                                        <FormLabel>{'Code'}</FormLabel>
                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                        >
                                            <FilledInput
                                                disabled
                                                placeholder='Code'
                                                value={master?.code ?? ''}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                {
                                    state.permissions.delete_attr &&
                                    <Grid item xs={6} lg={1} className={classes.buttonContainer}>
                                        <Tooltip title='Remove this master ticket' placement='right'>
                                            <IconButton onClick={() => removeMasterTicket(master.idticket_master)}>
                                                <Icon>{'delete'}</Icon>
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                }
                            </Grid>

                            <Grid container>
                                <Grid item xs={12} lg={3}>
                                    <FormGroup>
                                        <FormLabel>{'Supplier'}</FormLabel>
                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                        >
                                            <FilledInput
                                                disabled
                                                placeholder='Supplier'
                                                value={master?.supplier?.name ?? ''}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormGroup>
                                        <FormLabel>{'Land'}</FormLabel>
                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                        >
                                            <FilledInput
                                                disabled
                                                placeholder='Land'
                                                value={master?.land?.name ?? ''}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormGroup>
                                        <FormLabel>{'Location'}</FormLabel>
                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                        >
                                            <FilledInput
                                                disabled
                                                placeholder='Location'
                                                value={master?.location?.name ?? ''}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormGroup>
                                        <FormLabel>{'Total'}</FormLabel>
                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                        >
                                            <FilledInput
                                                disabled
                                                placeholder='Total'
                                                value={`$ ${master?.total ?? 0}`}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                            </Grid>

                            <h4>{'Breakdowns'}</h4>
                            <div className={classes.tableContainer}>
                                <DataGrid
                                    autoHeight
                                    pageSize={30}
                                    density='compact'
                                    columns={columns}
                                    rows={master.tickets}
                                    getRowId={row => row.idticket}
                                    hideFooter={master?.tickets?.length <= 30}
                                />
                            </div>

                            <Divider className={classes.divider} style={{ marginTop: 24 }} />
                        </div>
                    )
                    :
                    <span><i>{'There is no master tickets assigned'}</i></span>
            }
        </div>
    );
}

export default CarrierDetail;